import { getDAMProxyLink, getHighlightMarkup, getMuxUrl } from "@/core/utils";
import { Container } from "@components";
import { GetPostQuery } from "@graphqlTypes";
import className from "classnames/bind";
import { AnimatePresence, motion } from "motion/react";
import { Howl, Howler } from "howler";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./CantUnhearIt.module.scss";
import CantUnhearItBackground, {
  CantUnhearItBackgroundRefs,
} from "./CantUnhearItBackground/CantUnhearItBackground";
import { CantUnhearItHeader } from "./CantUnhearItHeader";
import { CantUnhearItHeaderRefs } from "./CantUnhearItHeader/CantUnhearItHeader";
import CantUnhearItTrack, {
  CantUnhearItTrackRefs,
} from "./CantUnhearItTrack/CantUnhearItTrack";
import CantUnhearItBullets from "./CantUnhearItBullets/CantUnhearItBullets";
import SwipeToBegin from "@/assets/svg/swipe-to-begin.svg";
import { useViewSize } from "@/providers/ViewSizeContext";
import { useClientMount } from "@/providers/ClientMountProvider";
import { useSoundContext } from "@/providers/SoundContext";
import { getBooleanParam } from "@/utils/urlParams";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const cx = className.bind(styles);

type CantUnhearItProps =
  GetPostQuery["rhPost"]["featureData"]["sonicConnections"];

export default function CantUnhearIt(props: CantUnhearItProps) {
  const { headline, subhead, tracks } = props;
  const { labels } = usePageWrapperData();
  const post = labels?.postTypeLabels?.post || "Post";
  const { scrollPrompt, swipePrompt } = labels || {};
  const headlineMarkup = headline ? getHighlightMarkup(headline) : null;
  const [animated, setAnimated] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [timelineInitialized, setTimelineInitialized] = useState(false);
  const { soundEnabled, globalVolume } = useSoundContext();
  const [currentTrackIndex, setCurrentTrackIndex] = useState(-1);
  const [tracksReady, setTracksReady] = useState(false);
  const containerEl = useRef<HTMLElement>(null);
  const lenisRef = useRef<Lenis>(null);
  const audioRefs = useRef<Howl[]>([]);
  const readyCount = useRef(0);
  const [arrayHeader, setArrayHeader] = useState([]);
  const backgroundEl = useRef<CantUnhearItBackgroundRefs>(null);
  const headerEl = useRef<CantUnhearItHeaderRefs>(null);
  const tracksEl = useRef<Array<CantUnhearItTrackRefs>>([]);
  const autoScrolling = useRef(false);
  const audioCreated = useRef(false);
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const isFirstChange = useRef(true);
  const audioFilters = useRef<
    { primary: BiquadFilterNode; secondary: BiquadFilterNode }[]
  >([]);
  const isLowPass = useRef(getBooleanParam("lowPass"));

  const setTrackRefs = useCallback(
    (trackEl: CantUnhearItTrackRefs | null, index: number) => {
      if (!trackEl) return;
      if (tracksEl.current[index] !== trackEl) {
        tracksEl.current[index] = trackEl;
        if (trackEl.getLastEl() && trackEl.getTrigger()) {
          readyCount.current += 1;
          if (readyCount.current === tracks.length) {
            setTracksReady(true);
          }
        }
      }
    },
    []
  );

  const handleAnimateIn = useCallback(() => {
    setAnimated(animated);
    setTimelineInitialized(true);
    lenisRef.current?.start();
  }, [lenisRef.current, setAnimated]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if ("scrollRestoration" in window?.history) {
        window.history.scrollRestoration = "manual";
      }
      window.scrollTo(0, 0);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 200) // Fix for intermittent "scroll position not being reset" issue. -Will
    }

    return () => {
      if ("scrollRestoration" in window?.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, []);

  useEffect(() => {
    // Wait for proper client-side initialization before creating audio
    if (
      !mounted ||
      !isReady ||
      audioCreated.current ||
      !headerEl?.current?.rootEl ||
      !timelineInitialized ||
      !tracksReady
    ) {
      return;
    }

    audioCreated.current = true;
    gsap.registerPlugin(ScrollTrigger);
    const staticTracks = [
      "/audio/kendrick.mp3",
      "/audio/monk.mp3",
      "/audio/ray.mp3",
    ];

    const audios = tracks.map((track, index) => {
      const audio = new Howl({
        src: window.location.search.includes("static")
          ? staticTracks[index]
          : getDAMProxyLink(track.audio),
        autoplay: true,
        loop: true,
        volume: 0, // Start at 0, will be controlled by updateAudioVolume
        mute: !soundEnabled,
        format: ["mp3"],
        html5: false,
      });

      return audio;
    });

    audioRefs.current = audios;
    // #region Low Pass Mode
    if (isLowPass.current) {
      const ctx = Howler.ctx;
      audioFilters.current = tracks.map(() => {
        const filter1 = ctx.createBiquadFilter();
        filter1.type = "lowpass";
        filter1.frequency.value = 0;
        filter1.Q.value = 15;

        const filter2 = ctx.createBiquadFilter();
        filter2.type = "lowpass";
        filter2.frequency.value = 250;
        filter2.Q.value = 1;
        filter1.connect(filter2);

        return { primary: filter1, secondary: filter2 };
      });
      audios.forEach((audio, index) => {
        // @ts-ignore - accessing private Howler internals
        const sound = audio._sounds?.[0];
        if (sound) {
          // @ts-ignore - accessing private Howler internals
          const node = sound._node;
          if (node && audioFilters.current[index]) {
            try {
              node.disconnect();
              node.connect(audioFilters.current[index].primary);
              audioFilters.current[index].primary.connect(ctx.destination);
            } catch (e) {
              console.warn("Could not connect filter:", e);
            }
          }
        }
      });
    }

    const lowPassAudioVolume = (
      index: number,
      progress: number,
      direction: number
    ) => {
      const lowestFrequency = 10;
      const highestFrequency = 24000 - lowestFrequency;
      let frequencyProgress = Math.abs(progress - 0.5) / 0.5;
      let extraProgress = progress;
      if (index > 0) {
        const value = (1 - progress) * globalVolume;
        audios[index - 1].volume(value);
        audioFilters.current[index - 1].primary.frequency.value =
          lowestFrequency + highestFrequency * frequencyProgress;
      }

      if (index === 0) {
        extraProgress = Math.min(1, progress * 2);
        frequencyProgress = Math.abs(extraProgress - 0.5) / 0.5;
      }

      audios[index].volume(extraProgress * globalVolume);
      audioFilters.current[index].primary.frequency.value =
        lowestFrequency + frequencyProgress * highestFrequency;
      // if (direction === -1 && index < tracks.length - 1 && index > 0) {
      //   audios[index + 1].volume(progress * globalVolume);
      //   audioFilters.current[index + 1].primary.frequency.value =
      //     lowestFrequency + frequencyProgress * highestFrequency;
      // }
    };
    // #endregion Low Pass Mode

    const updateAudioVolume = (
      index: number,
      progress: number,
      direction: number
    ) => {
      let realProgress = Math.min(1, progress * 3);
      // Apply global volume multiplier to all volume changes
      if (index > 0) {
        const value = (1 - realProgress) * globalVolume;
        audios[index - 1].volume(value);
        audios[index - 1].rate(Math.max(0.0625, 1 - realProgress));
      }
      audios[index].volume(realProgress * globalVolume);
      audios[index].rate(Math.max(0.0625, realProgress));
      if (direction === -1 && index < tracks.length - 1 && index > 0) {
        audios[index + 1].volume(realProgress * globalVolume);
        audios[index + 1].rate(Math.max(0.0625, realProgress));
      }
    };

    ScrollTrigger.create({
      trigger: headerEl.current.rootEl,
      start: "-90px top", //discount header height
      end: "bottom bottom",
      onUpdate: (self) => {
        if (!backgroundEl.current?.timeline) {
          return;
        }
        const timeline = backgroundEl.current.timeline;
        timeline?.seek(
          `stackImages+=${timeline.labels["fadeInDisc"] * self.progress}`,
          false
        );
      },
      onEnterBack: () => {
        setCurrentTrackIndex(-1);
      },
    });
    const lastTrack = tracksEl.current[tracksEl.current.length - 1];
    const lastPart = lastTrack.getLastEl();

    let initialized = false;

    ScrollTrigger.create({
      trigger: lastPart,
      start: "bottom bottom",
      end: "bottom top",
      onUpdate: (self) => {
        if (!backgroundEl.current?.containerEl) {
          return;
        }

        if (initialized) {
          gsap.set(backgroundEl.current.containerEl, {
            y: `${-self.progress * 100}vh`,
          });
          if (self.progress > 0) {
            setCurrentTrackIndex(-1);
          } else {
            setCurrentTrackIndex(tracks.length - 1);
          }
        }
      },
      onEnter: () => {
        initialized = true;
      },
    });
    tracksEl.current.forEach((track, index) => {
      const trigger = track.getTrigger();
      ScrollTrigger.create({
        trigger,
        start: "65px bottom",
        end: "bottom bottom",
        onEnter: () => {
          if (autoScrolling.current) {
            return;
          }
          setCurrentTrackIndex(index);
        },
        onEnterBack: () => {
          if (autoScrolling.current) {
            return;
          }
          setCurrentTrackIndex(index - 1);
        },
        onUpdate: (self) => {
          if (isLowPass.current) {
            lowPassAudioVolume(index, self?.progress, self?.direction);
          } else {
            updateAudioVolume(index, self?.progress, self?.direction);
          }

          const timeline = backgroundEl?.current?.timeline;
          if (!timeline) return;

          const startTrack = index === 0 ? "fadeInDisc" : `track-${index}`;
          const currentTrack = `track-${index + 1}`;

          let diff =
            timeline.labels[currentTrack] - timeline.labels[startTrack];

          if (index === tracksEl.current.length - 1) {
            diff = timeline.totalDuration() - timeline.labels[startTrack];
          }

          timeline?.seek(
            `${startTrack}+=${diff * self.progress * 0.95}`,
            false
          );
        },
        onLeave: () => {
          const timeline = backgroundEl.current?.timeline;
          const currentTrack = `track-${index + 1}`;
          timeline?.seek(timeline.labels[currentTrack], false);
        },
      });
    });
  }, [timelineInitialized, tracksReady, mounted, isReady]);

  useEffect(() => {
    // Don't initialize scroll/animations until properly mounted
    if (!mounted || !isReady) return;

    window.scrollTo(0, 0);
    lenisRef.current?.scrollTo(0, { immediate: true });

    lenisRef.current = new Lenis({
      autoRaf: false,
    });

    lenisRef.current.on("scroll", ({ scroll }) => {
      if (scrolled) return;

      if (scroll > 15) {
        setScrolled(true);
      }
    });
    function update(time: number) {
      lenisRef.current?.raf(time * 1000);
      ScrollTrigger.update();
    }

    lenisRef.current.stop();
    lenisRef.current.scrollTo(0);
    gsap.ticker.add(update);
    gsap.ticker.lagSmoothing(0);
    document.body.classList.add("cantunhearit");
    gsap.to(containerEl.current, { opacity: 1, delay: 0.2 });
    return () => {
      lenisRef.current?.destroy();
      gsap.ticker.remove(update);
      Howler.unload();
      document.body.classList.remove("cantunhearit");
    };
  }, [mounted, isReady]);

  useEffect(() => {
    audioRefs.current.forEach((audio) => audio.mute(!soundEnabled));
  }, [soundEnabled]);

  // Update all Howler audio volumes when global volume changes
  useEffect(() => {
    audioRefs.current.forEach((audio) => {
      const currentVolume = audio.volume();
      // Maintain the relative volume (0-1 range) but apply global volume multiplier
      if (currentVolume > 0) {
        audio.volume(currentVolume * globalVolume);
      }
    });
  }, [globalVolume]);

  const goToTrack = useCallback(
    (index: number) => {
      if (index === currentTrackIndex) return;

      autoScrolling.current = true;
      setCurrentTrackIndex(index);

      const trackEl = document.getElementById(`track-${index}`);
      if (trackEl && lenisRef.current) {
        const direction =
          lenisRef.current.targetScroll < trackEl.offsetTop ? "down" : "up";
        lenisRef.current.scrollTo(trackEl, {
          duration: 1,
          lock: true,
          offset: 100,
          onComplete: () => {
            autoScrolling.current = false;
          },
        });
      }
    },
    [currentTrackIndex]
  );

  const components = useMemo(
    () => [
      <CantUnhearItBackground
        tracks={tracks}
        onAnimateIn={handleAnimateIn}
        key="bg"
        ref={backgroundEl}
      />,
      <CantUnhearItHeader
        pillText={post}
        key="header"
        headlineMarkup={headlineMarkup}
        subhead={subhead}
        ref={headerEl}
      />,
    ],
    []
  );

  useEffect(() => {
    setArrayHeader(isDesktop ? components : [...components].reverse());
  }, [isDesktop]);

  useEffect(() => {
    if (!isReady) return;
    if (!isFirstChange.current) {
      window.location.reload();
    } else if (isFirstChange.current) {
      isFirstChange.current = false;
    }
  }, [isDesktop, isReady]);

  return (
    <section
      className={cx(["cant-unhear-it"])}
      ref={containerEl}
      style={{ opacity: 0 }}
    >
      <Container className={cx("cant-unhear-it-container")}>
        {arrayHeader}
        <AnimatePresence mode="wait">
          {currentTrackIndex !== -1 && (
            <CantUnhearItBullets
              goToTrack={goToTrack}
              tracks={tracks}
              currentTrackIndex={currentTrackIndex}
              initial={{ opacity: 0, x: "-15px" }}
              animate={{ opacity: 1, x: "0px" }}
              exit={{ opacity: 0, x: "-15px" }}
            />
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {!scrolled && (
            <motion.div
              className={cx("scroll-indicator")}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
            >
              <div className={cx("scroll-indicator-desktop")}>
                <div>{scrollPrompt}</div>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="#FF47B6"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M12 0C16.4183 0 20 3.58172 20 8V16C20 20.4183 16.4183 24 12 24C7.71994 24 4.22523 20.6389 4.01074 16.4121L4 16V8C4 3.58172 7.58172 0 12 0ZM12 2.5C8.96243 2.5 6.5 4.96243 6.5 8V16C6.5 19.0376 8.96243 21.5 12 21.5C15.0376 21.5 17.5 19.0376 17.5 16V8C17.5 4.96243 15.0376 2.5 12 2.5Z" />
                  <path
                    className={cx("wheel")}
                    d="M12 6C13.1046 6 14 6.89543 14 8C14 9.10457 13.1046 10 12 10C10.8954 10 10 9.10457 10 8C10 6.89543 10.8954 6 12 6Z"
                  />
                </svg>
              </div>

              <div className={cx("scroll-indicator-mobile")}>
                <div>{swipePrompt}</div>
                <SwipeToBegin />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className={cx("tracks")}>
          {tracks.map((track, index) => (
            <div key={`track-${index}`} id={`track-${index}`}>
              <CantUnhearItTrack
                track={track}
                first={index == 0}
                last={index === tracks.length - 1}
                ref={(trackEl) => setTrackRefs(trackEl, index)}
              />
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}
